import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import { createSpecialOrder, getSpecialOrder, updateSpecialOrder } from "../../services";
import {
  SpecialOrderDetail,
  SpecialOrderItem,
  SpecialOrderMode,
  SpecialOrderPriceList,
  SpecialOrderRoute,
} from "../../types/special-order.type";
import { featureAccess } from "../../permissions/access";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { formatNumber } from "../../utils";
import CustomerPicker, { CustomerOption } from "./CustomerPicker";
import CustomerTermsChips from "./CustomerTermsChips";
import TotalsSummary from "./TotalsSummary";
import { StatusChip } from "./Chips";
import { computeSpecialOrderTotals, lineTotal } from "./totals";
import { useBranches } from "./useBranches";
import { Prefill, useSpecialOrderPriceList } from "./useSpecialOrderPriceList";
import {
  API_DATE_FORMAT,
  MODE_LABEL,
  SPECIAL_ORDER_PATH,
  deliveryDateLabel,
  specialOrderDetailPath,
} from "./data";

interface SpecialOrderFormProps {
  /** Editing an existing draft. */
  uuid?: string;
  /** "ออกใหม่ (คัดลอกรายการ)": prefill a new draft from this (cancelled) order. */
  copyFrom?: string;
}

type FormErrors = Partial<Record<"branch" | "customer" | "route" | "date" | "items", string>>;

const HIGHLIGHT_BG = "#f4f5fe";

/** B → the branch's special route, anything else → the shop's own route; fall back to what exists. */
const defaultRouteUuid = (priceList: SpecialOrderPriceList): string => {
  const { main, special } = priceList.routes;
  const preferred = priceList.customer.service_mode === "B" ? special ?? main : main ?? special;
  return preferred?.uuid ?? "";
};

const toQtyMap = (
  items: SpecialOrderItem[],
  priceList: SpecialOrderPriceList,
): { qty: Record<string, string>; missing: SpecialOrderItem[] } => {
  const listed = new Set(priceList.items.map((item) => item.product_uuid));
  const qty: Record<string, string> = {};
  const missing: SpecialOrderItem[] = [];
  for (const item of items) {
    if (item.qty <= 0) continue;
    if (listed.has(item.product_uuid)) qty[item.product_uuid] = String(item.qty);
    else missing.push(item);
  }
  return { qty, missing };
};

function RouteOption({
  route,
  mode,
  selected,
  onSelect,
}: Readonly<{
  route: SpecialOrderRoute | null;
  mode: SpecialOrderMode;
  selected: boolean;
  onSelect: () => void;
}>) {
  const title = mode === "B" ? "สายพิเศษ" : "สายหลักของร้าน";
  return (
    <ButtonBase
      disabled={!route}
      onClick={onSelect}
      sx={{
        flex: 1,
        display: "block",
        textAlign: "left",
        p: 1.5,
        borderRadius: "10px",
        border: (theme) =>
          selected
            ? `2px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.divider}`,
        bgcolor: selected ? "#eceefd" : "#fff",
        opacity: route ? 1 : 0.5,
      }}
    >
      <Typography variant="body2" fontWeight={700} color={selected ? "primary" : "text.primary"}>
        {route ? `${title} ${route.route_code}` : `${title} (ไม่มี)`}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {MODE_LABEL[mode]}
        {route?.route_name ? ` · ${route.route_name}` : ""}
      </Typography>
    </ButtonBase>
  );
}

export default function SpecialOrderForm({ uuid, copyFrom }: SpecialOrderFormProps) {
  const navigate = useNavigate();
  const role = useUserRole();
  const canSelectBranch = featureAccess.specialOrderAllBranches(role);
  const branches = useBranches(canSelectBranch);
  const isEdit = Boolean(uuid);

  const [branchUuid, setBranchUuid] = useState<string>("");
  const [customer, setCustomer] = useState<CustomerOption | null>(null);
  const [routeUuid, setRouteUuid] = useState<string>("");
  const [deliveryDate, setDeliveryDate] = useState<Dayjs | null>(dayjs());
  const [qty, setQty] = useState<Record<string, string>>({});
  const [note, setNote] = useState<string>("");
  const [missingItems, setMissingItems] = useState<SpecialOrderItem[]>([]);
  const [itemSearch, setItemSearch] = useState<string>("");
  const [onlyWithQty, setOnlyWithQty] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [source, setSource] = useState<SpecialOrderDetail | null>(null);
  const [loadingSource, setLoadingSource] = useState<boolean>(Boolean(uuid || copyFrom));

  const applyPrefill = useCallback((data: SpecialOrderPriceList, prefill: Prefill) => {
    const routeUuids = [data.routes.main?.uuid, data.routes.special?.uuid];
    setRouteUuid(
      prefill.routeUuid && routeUuids.includes(prefill.routeUuid)
        ? prefill.routeUuid
        : defaultRouteUuid(data),
    );
    const mapped = toQtyMap(prefill.items ?? [], data);
    setQty(mapped.qty);
    setMissingItems(mapped.missing);
  }, []);
  const { priceList, loadingPriceList, loadPriceList, repriceForDay, clearPriceList } =
    useSpecialOrderPriceList(applyPrefill);

  // Edit / copy: read the source order, then its customer's CURRENT price list — an order
  // is always re-priced from the live pricebook when it is saved.
  useEffect(() => {
    const sourceUuid = uuid ?? copyFrom;
    if (!sourceUuid) return;
    let active = true;
    (async () => {
      setLoadingSource(true);
      try {
        const {
          data: { data },
        } = await getSpecialOrder(sourceUuid);
        if (!active || !data) return;
        setSource(data);
        setCustomer({
          uuid: data.customer.uuid,
          customer_code: data.customer.customer_code,
          name: data.customer.name,
        });
        setNote(data.note ?? "");
        // A copy starts on today like a new order; a draft keeps its day unless it has passed.
        const stored = dayjs(data.delivery_date);
        const deliveryDay = uuid && !stored.isBefore(dayjs(), "day") ? stored : dayjs();
        if (uuid) setDeliveryDate(deliveryDay);
        await loadPriceList(data.customer.uuid, deliveryDay, {
          routeUuid: data.route.uuid,
          items: data.items,
        });
      } finally {
        if (active) setLoadingSource(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [uuid, copyFrom, loadPriceList]);

  // Head office searches customers inside a branch; an existing order sets it from its tenant.
  useEffect(() => {
    if (!canSelectBranch || !source || branchUuid) return;
    const branch = branches.find(
      (candidate) => String(candidate.company_id) === String(source.company_id),
    );
    if (branch) setBranchUuid(branch.uuid);
  }, [canSelectBranch, source, branches, branchUuid]);

  const handleChangeCustomer = (selected: CustomerOption | null) => {
    setCustomer(selected);
    setErrors((prev) => ({ ...prev, customer: undefined, route: undefined, items: undefined }));
    clearPriceList();
    setRouteUuid("");
    setQty({});
    setMissingItems([]);
    if (selected) loadPriceList(selected.uuid, deliveryDate);
  };

  const handleChangeDeliveryDate = (value: Dayjs | null) => {
    setDeliveryDate(value);
    setErrors((prev) => ({ ...prev, date: undefined }));
    // A half-typed date is not a day yet; the prices are read once it is one.
    if (customer && value?.isValid()) repriceForDay(customer.uuid, value);
  };

  const handleChangeBranch = (value: string) => {
    setBranchUuid(value);
    setErrors((prev) => ({ ...prev, branch: undefined }));
    handleChangeCustomer(null);
  };

  const mode: SpecialOrderMode | null = useMemo(() => {
    if (!priceList || !routeUuid) return null;
    return priceList.routes.special?.uuid === routeUuid ? "B" : "A";
  }, [priceList, routeUuid]);

  const lines = useMemo(
    () =>
      (priceList?.items ?? []).map((item) => ({
        ...item,
        qty: Number(qty[item.product_uuid] || 0),
      })),
    [priceList, qty],
  );

  const totals = useMemo(
    () =>
      computeSpecialOrderTotals(
        lines,
        priceList?.customer.discount_percent ?? null,
        priceList?.customer.taxtype ?? null,
      ),
    [lines, priceList],
  );

  const visibleLines = useMemo(() => {
    const text = itemSearch.trim().toLowerCase();
    return lines
      .map((line, index) => ({ line, no: index + 1 }))
      .filter(({ line }) => !onlyWithQty || line.qty > 0)
      .filter(
        ({ line }) =>
          !text ||
          line.product_name.toLowerCase().includes(text) ||
          line.product_code.toLowerCase().includes(text) ||
          (line.barcode ?? "").includes(text),
      );
  }, [lines, itemSearch, onlyWithQty]);

  const handleQtyChange = (productUuid: string, value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 5);
    setQty((prev) => {
      const next = { ...prev };
      if (!digits || Number(digits) === 0) delete next[productUuid];
      else next[productUuid] = String(Number(digits));
      return next;
    });
    setErrors((prev) => ({ ...prev, items: undefined }));
  };

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (canSelectBranch && !branchUuid && !isEdit) next.branch = "กรุณาเลือกสาขา";
    if (!customer) next.customer = "กรุณาเลือกลูกค้า";
    if (!routeUuid) next.route = "กรุณาเลือกสาย";
    if (!deliveryDate || !deliveryDate.isValid()) next.date = "กรุณาเลือกวันที่";
    else if (deliveryDate.isBefore(dayjs(), "day")) next.date = "เลือกวันที่ย้อนหลังไม่ได้";
    if (totals.lineCount === 0) next.items = "กรุณากรอกจำนวนสินค้าอย่างน้อย 1 รายการ";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !customer || !deliveryDate) return;
    const body = {
      customer_uuid: customer.uuid,
      route_uuid: routeUuid,
      delivery_date: deliveryDate.format(API_DATE_FORMAT),
      items: lines
        .filter((line) => line.qty > 0)
        .map((line) => ({ product_uuid: line.product_uuid, qty: line.qty })),
      note: note.trim() || null,
    };
    setSaving(true);
    try {
      const response = uuid
        ? await updateSpecialOrder(uuid, body)
        : await createSpecialOrder(body);
      const savedUuid = response.data?.data?.uuid ?? uuid;
      navigate(savedUuid ? specialOrderDetailPath(savedUuid) : SPECIAL_ORDER_PATH);
    } catch {
      // The interceptor already shows the API's reason (422 route/date/lines, 409 status).
    } finally {
      setSaving(false);
    }
  };

  if (loadingSource) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (isEdit && source && source.status !== "draft") {
    return (
      <Alert
        severity="warning"
        action={
          <Button component={RouterLink} to={specialOrderDetailPath(source.uuid)} size="small">
            ดูรายละเอียด
          </Button>
        }
      >
        ออเดอร์ {source.order_no} ไม่ได้เป็นร่างแล้ว แก้ไขไม่ได้
      </Alert>
    );
  }

  const discountPercent = priceList?.customer.discount_percent ?? null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Typography variant="subtitle1" fontWeight={700}>
                ลูกค้าและการส่ง
              </Typography>
              <StatusChip status="draft" />
            </Box>

            {copyFrom && source && (
              <Alert severity="info" sx={{ mb: 2 }}>
                คัดลอกรายการจากออเดอร์ {source.order_no} ตรวจจำนวนและวันที่ก่อนบันทึก
              </Alert>
            )}

            <Grid container spacing={2}>
              {canSelectBranch && (
                <Grid size={{ xs: 12, sm: 5 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="สาขา"
                    value={branchUuid}
                    disabled={isEdit}
                    onChange={(event) => handleChangeBranch(event.target.value)}
                    error={Boolean(errors.branch)}
                    helperText={errors.branch || "ค้นหาลูกค้าในสาขาที่เลือก"}
                  >
                    <MenuItem value="">เลือกสาขา</MenuItem>
                    {branches.map((branch) => (
                      <MenuItem key={branch.uuid} value={branch.uuid}>
                        {branch.branch_code} - {branch.branch_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: canSelectBranch ? 7 : 12 }}>
                <CustomerPicker
                  value={customer}
                  onChange={handleChangeCustomer}
                  branchUuid={branchUuid || undefined}
                  disabled={canSelectBranch && !branchUuid && !customer}
                  error={errors.customer}
                  helperText={
                    canSelectBranch && !branchUuid && !customer ? "เลือกสาขาก่อน" : undefined
                  }
                />
              </Grid>
            </Grid>

            {loadingPriceList && (
              <Box display="flex" alignItems="center" gap={1} mt={2}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  กำลังโหลดสมุดราคาของลูกค้า...
                </Typography>
              </Box>
            )}

            {priceList && (
              <>
                <Box mt={2}>
                  <CustomerTermsChips customer={priceList.customer} />
                </Box>

                <Typography variant="body2" fontWeight={600} mt={3} mb={1}>
                  ออกบิลในสาย (ค่าเริ่มต้นตามแบบบริการของลูกค้า)
                </Typography>
                <Box display="flex" gap={1.5} flexWrap="wrap">
                  <RouteOption
                    route={priceList.routes.special}
                    mode="B"
                    selected={Boolean(routeUuid) && routeUuid === priceList.routes.special?.uuid}
                    onSelect={() => {
                      setRouteUuid(priceList.routes.special?.uuid ?? "");
                      setErrors((prev) => ({ ...prev, route: undefined }));
                    }}
                  />
                  <RouteOption
                    route={priceList.routes.main}
                    mode="A"
                    selected={Boolean(routeUuid) && routeUuid === priceList.routes.main?.uuid}
                    onSelect={() => {
                      setRouteUuid(priceList.routes.main?.uuid ?? "");
                      setErrors((prev) => ({ ...prev, route: undefined }));
                    }}
                  />
                </Box>
                {errors.route && (
                  <Typography variant="caption" color="error">
                    {errors.route}
                  </Typography>
                )}

                <Grid container spacing={2} mt={1}>
                  <Grid size={{ xs: 12, sm: 5 }}>
                    <DatePicker
                      label={deliveryDateLabel(mode ?? "A")}
                      value={deliveryDate}
                      format="DD/MM/YYYY"
                      minDate={dayjs()}
                      onChange={handleChangeDeliveryDate}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          error: Boolean(errors.date),
                          helperText:
                            errors.date ||
                            (mode === "B"
                              ? "ระบบเบิกของเข้าสายพิเศษของวันนั้นเมื่อยืนยันส่ง"
                              : "ของต้องอยู่ในใบโหลดรถของวันนั้น"),
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 7 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="หมายเหตุ"
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                      slotProps={{ htmlInput: { maxLength: 500 } }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>

          {priceList && (
            <Paper sx={{ p: 0, overflow: "hidden" }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
                p={2}
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  สินค้า (ทั้งหมดในสมุดราคาของลูกค้า · {priceList.items.length} รายการ)
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={onlyWithQty}
                        onChange={(event) => setOnlyWithQty(event.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">เฉพาะที่มีจำนวน</Typography>}
                  />
                  <TextField
                    size="small"
                    placeholder="ค้นหาสินค้า / บาร์โค้ด"
                    value={itemSearch}
                    onChange={(event) => setItemSearch(event.target.value)}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Box>
              </Box>

              {missingItems.length > 0 && (
                <Alert severity="warning" sx={{ mx: 2, mb: 2 }}>
                  สินค้าเหล่านี้ไม่อยู่ในสมุดราคาของลูกค้าแล้ว จะไม่ถูกบันทึก:{" "}
                  {missingItems.map((item) => `${item.product_name} (${item.qty})`).join(", ")}
                </Alert>
              )}
              {errors.items && (
                <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
                  {errors.items}
                </Alert>
              )}

              <TableContainer sx={{ maxHeight: "60vh" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={48}>#</TableCell>
                      <TableCell>สินค้า</TableCell>
                      <TableCell>บาร์โค้ด</TableCell>
                      <TableCell align="right">ราคา/หน่วย</TableCell>
                      <TableCell align="center" width={110}>
                        จำนวน
                      </TableCell>
                      <TableCell align="right">จำนวนเงิน</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleLines.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {priceList.items.length === 0
                              ? "สมุดราคาของลูกค้านี้ไม่มีสินค้าที่ขายได้"
                              : "ไม่พบสินค้าตามเงื่อนไข"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {visibleLines.map(({ line, no }) => (
                      <TableRow
                        key={line.product_uuid}
                        sx={{ bgcolor: line.qty > 0 ? HIGHLIGHT_BG : undefined }}
                      >
                        <TableCell>{no}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={line.qty > 0 ? 600 : 400}>
                            {line.product_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {line.product_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {line.barcode || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(line.unit_price)}</TableCell>
                        <TableCell align="center">
                          <TextField
                            size="small"
                            value={qty[line.product_uuid] ?? ""}
                            placeholder="0"
                            onChange={(event) =>
                              handleQtyChange(line.product_uuid, event.target.value)
                            }
                            slotProps={{
                              htmlInput: {
                                inputMode: "numeric",
                                style: { textAlign: "right" },
                                "aria-label": `จำนวน ${line.product_name}`,
                              },
                            }}
                            sx={{ width: 90 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {line.qty > 0 ? formatNumber(lineTotal(line)) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ position: { md: "sticky" }, top: { md: 16 } }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              สรุปยอด
            </Typography>
            {mode && (
              <Typography variant="body2" color="text.secondary" mb={1}>
                {MODE_LABEL[mode]}
              </Typography>
            )}
            <TotalsSummary
              lineCount={totals.lineCount}
              subtotal={totals.subtotal}
              discountPercent={discountPercent}
              discountAmount={totals.discountAmount}
              totalNoTax={totals.totalNoTax}
              taxAmount={totals.taxAmount}
              grandTotal={totals.grandTotal}
            />
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              ยอดนี้คำนวณเบื้องต้นบนหน้าจอ ระบบคำนวณยอดจริงอีกครั้งเมื่อบันทึก
            </Typography>
            <Box display="flex" flexDirection="column" gap={1} mt={2}>
              <Button
                variant="contained"
                loading={saving}
                disabled={!priceList || loadingPriceList}
                onClick={handleSave}
              >
                บันทึกร่าง
              </Button>
              <Button
                variant="text"
                disabled={saving}
                onClick={() =>
                  navigate(uuid ? specialOrderDetailPath(uuid) : SPECIAL_ORDER_PATH)
                }
              >
                ยกเลิก
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
}
